import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import {
  ClientConversation,
  ClientMessage,
  ConversationPreview,
  ConversationStatus,
  FormValues,
  MessageStatus,
  ChatBlockType,
  ChatMessageType,
} from './types';
import { ChatRole } from '../../types/ChatRoles';
import { getCompaniesByIds } from '../../api/companyApi';
import { setSelectedCompanies } from '../company/slice';

// Global constants
export const DAILY_MESSAGE_LIMIT = 50;

type ModalState =
  | {
      type: 'rename' | 'delete';
      conversationId: string;
    }
  | undefined;

interface ChatState {
  formValues: FormValues;
  conversation: ClientConversation | null;
  conversationHistory: ConversationPreview[];
  dailyMessageCount: number;
  limitHitTime?: string;
  resetTime?: string;
  isAdminOpen: boolean;
  isMobileHistoryOpen: boolean;
  isLoadingHistory: boolean;
  isLoadingConversation: boolean;
  modal: ModalState;
  thinkingMessage?: {
    sequence: number;
    thinkingStatus: string;
  };
  inputMessage: string;
}

const initialState: ChatState = {
  formValues: {
    model: undefined,
    topK: undefined,
    ragMode: undefined,
    relevancePercent: undefined,
    traceTag: undefined,
    forceQueryType: undefined,
    filteredCompanyIds: undefined,
  },
  conversation: null,
  conversationHistory: [],
  dailyMessageCount: 0,
  limitHitTime: undefined,
  resetTime: undefined,
  isAdminOpen: false,
  isMobileHistoryOpen: false,
  isLoadingHistory: false,
  isLoadingConversation: false,
  modal: undefined,
  thinkingMessage: undefined,
  inputMessage: '',
};

export const chatSlice = createSlice({
  name: 'chat',
  initialState,
  reducers: {
    // Modal state actions
    openModal: (
      state,
      action: PayloadAction<{ type: 'rename' | 'delete'; conversationId: string }>,
    ) => {
      state.modal = action.payload;
    },
    closeModal: (state) => {
      state.modal = undefined;
    },
    setConversationHistory: (
      state,
      action: PayloadAction<{
        conversations: ConversationPreview[];
      }>,
    ) => {
      state.conversationHistory = action.payload.conversations;
    },
    addToHistory: (state, action: PayloadAction<ConversationPreview>) => {
      // sometimes conversation is passed through, so just get the preview fields
      const preview: ConversationPreview = {
        id: action.payload.id,
        title: action.payload.title,
        created_at: action.payload.created_at,
        updated_at: action.payload.updated_at,
        status: action.payload.status,
        filtered_company_ids: action.payload.filtered_company_ids,
        filtered_company_tickers: action.payload.filtered_company_tickers,
      };

      // Add to history if not already present, otherwise update
      const index = state.conversationHistory.findIndex((c) => c.id === preview.id);
      if (index >= 0) {
        state.conversationHistory[index] = preview;
      } else {
        state.conversationHistory.unshift(preview);
      }
    },
    updateConversationInHistory: (
      state,
      action: PayloadAction<{
        id: string;
        updates: Partial<ClientConversation>;
      }>,
    ) => {
      const { id, updates } = action.payload;
      const index = state.conversationHistory.findIndex((c) => c.id === id);

      // Only pick ConversationPreview fields for history
      const previewFields = [
        'title',
        'updated_at',
        'status',
        'is_deleted',
        'filtered_company_ids',
        'filtered_company_tickers',
      ];
      const previewUpdates = Object.fromEntries(
        Object.entries(updates).filter(([key]) => previewFields.includes(key)),
      );

      if (index >= 0) {
        state.conversationHistory[index] = {
          ...state.conversationHistory[index],
          ...previewUpdates,
        };
      }

      // Also update current conversation if it's affected
      if (state.conversation && state.conversation.id === id) {
        state.conversation = {
          ...state.conversation,
          ...updates,
        };
      }
    },
    removeConversationFromHistory: (state, action: PayloadAction<string>) => {
      const conversationId = action.payload;
      state.conversationHistory = state.conversationHistory.filter((c) => c.id !== conversationId);

      // Clear current conversation if it's the one being removed
      if (state.conversation && state.conversation.id === conversationId) {
        state.conversation = null;
      }
    },
    updateFormValues: (state, action: PayloadAction<Partial<FormValues>>) => {
      state.formValues = { ...state.formValues, ...action.payload };
    },
    resetFormValues: (state) => {
      state.formValues = initialState.formValues;
    },
    setConversation: (state, action: PayloadAction<{ conversation: ClientConversation }>) => {
      state.conversation = action.payload.conversation;

      // Set filteredCompanyIds in formValues if they exist in the conversation
      if (action.payload.conversation.filtered_company_ids) {
        state.formValues.filteredCompanyIds = action.payload.conversation.filtered_company_ids;
      }
    },
    clearConversation: (state) => {
      state.conversation = null;
      // Clear filteredCompanyIds when clearing conversation
      state.formValues.filteredCompanyIds = undefined;
    },
    addMessage: (state, action: PayloadAction<ClientMessage>) => {
      // Initialize a conversation if it doesn't exist yet
      if (!state.conversation) {
        const query = action.payload.blocks[0].content;
        const title = query ? (query.length > 50 ? `${query.substring(0, 47)}...` : query) : '';
        state.conversation = {
          id: '',
          user_id: '',
          title,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          status: ConversationStatus.Inactive,
          messages: [action.payload],
        };
      } else {
        state.conversation.messages.push(action.payload);
        state.conversation.updated_at = new Date().toISOString();
      }
    },
    setConversationStats: (
      state,
      action: PayloadAction<{
        dailyMessageCount: number;
        limitHitTime?: string;
        resetTime?: string;
      }>,
    ) => {
      state.dailyMessageCount = action.payload.dailyMessageCount;
      state.limitHitTime = action.payload.limitHitTime;
      state.resetTime = action.payload.resetTime;
    },
    setDailyMessageCount: (state, action: PayloadAction<number>) => {
      state.dailyMessageCount = action.payload;
    },
    incrementDailyMessageCount: (state) => {
      state.dailyMessageCount += 1;
    },
    setIsLoadingHistory: (state, action: PayloadAction<boolean>) => {
      state.isLoadingHistory = action.payload;
    },
    // Mobile state actions
    toggleAdmin: (state) => {
      state.isAdminOpen = !state.isAdminOpen;
    },
    openMobileHistory: (state) => {
      state.isMobileHistoryOpen = true;
    },
    closeMobileHistory: (state) => {
      state.isMobileHistoryOpen = false;
    },
    setIsLoadingConversation: (state, action: PayloadAction<boolean>) => {
      state.isLoadingConversation = action.payload;
    },
    updateMessageStatus: (
      state,
      action: PayloadAction<{
        conversationId: string;
        messageIndex: number;
        status: MessageStatus;
      }>,
    ) => {
      if (state.conversation?.id === action.payload.conversationId) {
        const { messageIndex, status } = action.payload;
        if (messageIndex >= 0 && messageIndex < state.conversation.messages.length) {
          state.conversation.messages[messageIndex].status = status;
        }
      }
    },
    updateThinkingStatus: (
      state,
      action: PayloadAction<{
        conversationId: string;
        userMessageSequence: number;
        thinkingStatus: string;
      }>,
    ) => {
      if (state.conversation?.id === action.payload.conversationId) {
        const { userMessageSequence, thinkingStatus } = action.payload;

        if (state.conversation.status === ConversationStatus.Answering) {
          state.thinkingMessage = {
            sequence: userMessageSequence,
            thinkingStatus,
          };
        }
      }
    },
    setInputMessage: (state, action: PayloadAction<string>) => {
      state.inputMessage = action.payload;
    },
    clearInputMessage: (state) => {
      state.inputMessage = '';
    },
  },
});

export const createErrorMessage = (
  message: string = 'Sorry, there was an error processing your request. Please try again.',
): ClientMessage => {
  return {
    timestamp: new Date().toISOString(),
    role: ChatRole.Assistant,
    sequence: 0,
    type: ChatMessageType.Chat,
    blocks: [
      {
        sequence: 0,
        type: ChatBlockType.Text,
        content: message,
        sources: [],
      },
    ],
    sources: [],
  };
};

export const loadConversation = createAsyncThunk(
  'chat/loadConversation',
  async (conversation: ClientConversation, { dispatch }) => {
    dispatch(chatSlice.actions.setConversation({ conversation }));

    const { filtered_company_ids } = conversation;

    if (filtered_company_ids && filtered_company_ids.length > 0) {
      try {
        const companies = await getCompaniesByIds(filtered_company_ids);
        dispatch(setSelectedCompanies(companies));
      } catch (error) {
        console.error('Failed to fetch companies for conversation', error);
        // Optionally dispatch an error action
      }
    }
  },
);

export const {
  updateFormValues,
  resetFormValues,
  setConversation,
  addMessage,
  clearConversation,
  setConversationStats,
  setConversationHistory,
  setDailyMessageCount,
  incrementDailyMessageCount,
  setIsLoadingHistory,
  addToHistory,
  toggleAdmin,
  openMobileHistory,
  closeMobileHistory,
  setIsLoadingConversation,
  updateMessageStatus,
  updateThinkingStatus,
  openModal,
  closeModal,
  updateConversationInHistory,
  removeConversationFromHistory,
  setInputMessage,
  clearInputMessage,
} = chatSlice.actions;

export default chatSlice.reducer;
